import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { gstAPI, gstSetToken } from '../../services/api';
import { GstProvider } from './GstContext';
import GstLockScreen from './GstLockScreen';
import GstInvoicesTab from './GstInvoicesTab';
import GstNotesTab from './GstNotesTab';
import GstMasterTabs from './GstMasterTabs';
import GstReportsTab from './GstReportsTab';
import GstCompanyTab from './GstCompanyTab';
import GstDeliveryBanner from './GstDeliveryBanner';
import { msg } from './gstUi';
import useDarkReady from '../../theme/useDarkReady';

const IDLE_MS = 10 * 60 * 1000;
const TABS = [
  ['invoices', 'Invoices'], ['notes', 'Credit/Debit Notes'], ['parties', 'Parties'],
  ['items', 'Items'], ['reports', 'Reports'], ['company', 'Company'],
];

export default function GstBillingPage() {
  useDarkReady(); // opt in to the CRM dark theme (all GST colours come from shared tokens)
  const [phase, setPhase] = useState('loading'); // loading | setup | locked | open
  const [tab, setTab] = useState('invoices');
  const [remaining, setRemaining] = useState(IDLE_MS);
  const [issues, setIssues] = useState([]);
  const lastActive = useRef(Date.now());
  const lastPing = useRef(Date.now());
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const refresh = useCallback(async () => {
    try {
      const s = (await gstAPI.status()).data.data;
      lastActive.current = Date.now();
      setPhase(!s.passwordSet ? 'setup' : s.unlocked ? 'open' : 'locked');
    } catch (e) {
      toast.error(msg(e));
      setPhase('locked');
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const lock = useCallback(async (silent) => {
    if (phaseRef.current === 'open') { try { await gstAPI.lock(); } catch { /* already locked */ } }
    gstSetToken('');
    setPhase('locked');
    if (!silent) toast.success('GST section locked');
  }, []);

  // Lock when the admin navigates away from the page.
  useEffect(() => () => { if (phaseRef.current === 'open') { gstAPI.lock().catch(() => {}); gstSetToken(''); } }, []);

  // Idle countdown + keep-alive: the server expires the session after 10 idle minutes, so while the
  // admin is active (e.g. typing a long invoice) ping every 30 s to keep it open.
  useEffect(() => {
    if (phase !== 'open') return undefined;
    const t = setInterval(async () => {
      const idle = Date.now() - lastActive.current;
      setRemaining(Math.max(IDLE_MS - idle, 0));
      if (idle >= IDLE_MS) { lock(false); return; }
      if (Date.now() - lastPing.current > 30000) {
        lastPing.current = Date.now();
        try { const s = (await gstAPI.status()).data.data; if (!s.unlocked) lock(true); } catch { /* transient */ }
      }
    }, 1000);
    return () => clearInterval(t);
  }, [phase, lock]);

  // Delivery problems (failed / retrying / changed since sent) show as a banner on every GST screen.
  useEffect(() => {
    if (phase !== 'open') return undefined;
    const load = () => gstAPI.delivery().then((r) => setIssues(r.data.data.dispatches)).catch(() => {});
    load();
    const t = setInterval(load, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [phase]);

  const touch = () => { lastActive.current = Date.now(); };

  // A 423 from any call means the server closed the gate: show the lock screen.
  const callRaw = useCallback(async (fn) => {
    try { lastActive.current = Date.now(); return await fn(); } catch (e) {
      if (e.response?.status === 423) { gstSetToken(''); setPhase('locked'); toast.error('GST section locked — enter the password'); }
      throw e;
    }
  }, []);
  const call = useCallback(async (fn) => (await callRaw(fn)).data, [callRaw]);

  if (phase === 'loading') return <div className="spinner" style={{ margin: 60 }} />;
  if (phase !== 'open') {
    return <GstLockScreen key={phase} phase={phase} onUnlocked={() => { lastActive.current = Date.now(); setPhase('open'); }} onReset={refresh} />;
  }
  const mm = String(Math.floor(remaining / 60000)).padStart(2, '0');
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, '0');
  return (
    <GstProvider value={{ call, callRaw }}>
      <div onClick={touch} onKeyDown={touch} onMouseMove={touch}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0 }}>GST Billing</h2>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 12, opacity: 0.7 }}>Auto-locks in {mm}:{ss}</span>
            <button className="btn btn-secondary" onClick={() => lock(false)}><Lock size={14} /> Lock</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {TABS.map(([k, label]) => (
            <button key={k} className={`btn ${tab === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
        <GstDeliveryBanner dispatches={issues} onOpen={() => setTab('company')} />
        {tab === 'invoices' && <GstInvoicesTab />}
        {tab === 'notes' && <GstNotesTab />}
        {tab === 'parties' && <GstMasterTabs kind="parties" />}
        {tab === 'items' && <GstMasterTabs kind="items" />}
        {tab === 'reports' && <GstReportsTab />}
        {tab === 'company' && <GstCompanyTab />}
      </div>
    </GstProvider>
  );
}
